/**
 * Shared Zod schemas for nested **patient / profile** blocks on assessment info steps.
 *
 * Reused across assessment variants (care, home care, rehab, psych, hospital,
 * mobile short, medical supplies, elective rehab). Import from `core/schemas/assessment`
 * instead of copying identical `z.object(...)` blocks into each step's `schema.ts`.
 *
 * Root form fields (`assessment_variant`, `solutions`, …) live in
 * `assessmentFieldSchemas.ts`. Step-specific shapes remain in each variant's `schema.ts`.
 */
import { trimmedString } from "core/schemas/utils";
import { z } from "zod";

export const encryptedFieldSchema = z
  .object({ decrypted: trimmedString().nullish() })
  .nullish();

export const guardianSchema = z.object({
  city: encryptedFieldSchema,
  first_name: encryptedFieldSchema,
  house_number: encryptedFieldSchema,
  last_name: encryptedFieldSchema,
  phone: encryptedFieldSchema,
  responsibilities: encryptedFieldSchema,
  street: encryptedFieldSchema,
  type: z.number().nullish(),
  zipcode: encryptedFieldSchema,
});

export const relativeSchema = z.object({
  city: encryptedFieldSchema,
  first_name: encryptedFieldSchema,
  house_number: encryptedFieldSchema,
  last_name: encryptedFieldSchema,
  phone: encryptedFieldSchema,
  relationship: z
    .object({
      decrypted: z.preprocess((val) => {
        if (Array.isArray(val)) {
          return val[0] != null ? String(val[0]) : null;
        }
        return val;
      }, trimmedString().nullish()),
    })
    .nullish(),
  relationship_other: encryptedFieldSchema,
  street: encryptedFieldSchema,
  zipcode: encryptedFieldSchema,
});

function preprocessBirthDateDecrypted(val: unknown): Date | undefined {
  if (!val) return undefined;
  if (val instanceof Date) return val;
  if (typeof val === "number") return new Date(val * 1000);
  if (typeof val === "string") {
    const parsed = new Date(val);
    return isNaN(parsed.getTime()) ? undefined : parsed;
  }
  return undefined;
}

export const assessmentBirthDateSchema = z
  .object({
    decrypted: z
      .preprocess(preprocessBirthDateDecrypted, z.date().nullish())
      .nullish(),
  })
  .nullish();

export const assessmentSearchLocationSchema = z
  .object({
    city: trimmedString().nullish(),
    encrypted_house_number: z
      .object({ decrypted: trimmedString().nullish() })
      .nullish(),
    federal_state: trimmedString().nullish(),
    floor: trimmedString().nullish(),
    latitude: z.number().nullish(),
    longitude: z.number().nullish(),
    radius_in_meter: z.number().nullish(),
    street: trimmedString().nullish(),
    zipcode: trimmedString().nullish(),
  })
  .nullish();

export const assessmentSearchProfileSchema = z
  .object({
    _address_input_mode: z.enum(["search", "manual"]).nullish(),
    _location: trimmedString().nullish(),
    search_location: assessmentSearchLocationSchema,
  })
  .optional();

export const generalPractitionerSchema = z.object({
  city: encryptedFieldSchema,
  contact_details: trimmedString().nullish(),
  first_name: encryptedFieldSchema,
  general_practitioner: encryptedFieldSchema,
  honorific_prefix: encryptedFieldSchema,
  honorific_suffix: encryptedFieldSchema,
  house_number: encryptedFieldSchema,
  job_title: encryptedFieldSchema,
  last_name: encryptedFieldSchema,
  name: trimmedString().nullish(),
  phone: encryptedFieldSchema,
  street: encryptedFieldSchema,
  zipcode: encryptedFieldSchema,
});
